/**
 * Navigation payload from user-service profile (`navigation` field).
 */

export interface NavigationView {
  slug: string;
  label: string;
  icon: string | null;
  defaultPath: string;
  routePatterns: string[];
}

export interface NavigationModule {
  slug: string;
  label: string;
  description: string | null;
  icon: string | null;
  views: NavigationView[];
}

export interface NavigationPayload {
  modules: NavigationModule[];
}

function normalizeView(raw: Record<string, unknown>): NavigationView {
  const routes = raw.routePatterns ?? raw.route_patterns;
  return {
    slug: String(raw.slug ?? ""),
    label: String(raw.label ?? ""),
    icon: raw.icon != null ? String(raw.icon) : null,
    defaultPath: String(raw.defaultPath ?? raw.default_path ?? ""),
    routePatterns: Array.isArray(routes)
      ? routes.map((r) => String(r)).filter(Boolean)
      : [],
  };
}

function normalizeModule(raw: Record<string, unknown>): NavigationModule {
  const viewRows = raw.views;
  return {
    slug: String(raw.slug ?? ""),
    label: String(raw.label ?? ""),
    description:
      raw.description != null ? String(raw.description) : null,
    icon: raw.icon != null ? String(raw.icon) : null,
    views: Array.isArray(viewRows)
      ? viewRows.map((v) => normalizeView(v as Record<string, unknown>))
      : [],
  };
}

export function normalizeNavigationPayload(
  raw: unknown,
): NavigationPayload {
  if (!raw || typeof raw !== "object") {
    return { modules: [] };
  }
  const row = raw as Record<string, unknown>;
  const modulesRaw = row.modules;
  return {
    modules: Array.isArray(modulesRaw)
      ? modulesRaw.map((m) => normalizeModule(m as Record<string, unknown>))
      : [],
  };
}

export function getEnabledModuleSlugs(navigation: NavigationPayload): string[] {
  return navigation.modules.map((m) => m.slug).filter(Boolean);
}
